import {
  bookingQueryOptions,
  bookingQueryKeys,
  refreshBookingDetailCache,
  refreshProfileBookingsCache,
} from '@/hooks/queries/booking.queries';
import { normalizeProfileBooking, normalizeProfileBookingsResponse, sortProfileBookings } from '@/lib/profile-bookings';
import { BookingService } from '@/services/booking.service';
import type { BookingListSection } from '@/types/dto/booking.dto';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export function useProfileBookings(section: BookingListSection, viewerId?: string | null, enabled = true) {
  const { data, isError, isFetching, isLoading, refetch } = useQuery(
    bookingQueryOptions.profile(section, viewerId, enabled),
  );
  const normalizedResponse = normalizeProfileBookingsResponse(data);

  return {
    bookings: sortProfileBookings(normalizedResponse.bookings, section),
    counters: normalizedResponse.counters,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
}

export function useProfileBookingDetail(bookingId?: string | null, enabled = true) {
  const { data, error, isError, isFetching, isLoading, refetch } = useQuery(
    bookingQueryOptions.detail(bookingId, enabled),
  );

  return {
    booking: normalizeProfileBooking(data),
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
}

export function useConfirmProfileBooking(viewerId?: string | null) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (bookingId: string) => BookingService.confirm(bookingId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: bookingQueryKeys.all });
      await refreshProfileBookingsCache(queryClient, viewerId);
    },
  });

  return {
    confirmBooking: mutation.mutateAsync,
    error: mutation.error,
    isError: mutation.isError,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
  };
}

export function useConfirmBookingDetail(bookingId?: string | null, viewerId?: string | null) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (id: string) => BookingService.confirm(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: bookingQueryKeys.all });
      await Promise.all([
        refreshBookingDetailCache(queryClient, bookingId),
        refreshProfileBookingsCache(queryClient, viewerId),
      ]);
    },
  });

  return {
    confirmBooking: mutation.mutateAsync,
    error: mutation.error,
    isError: mutation.isError,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
  };
}

export function useCancelProfileBooking(viewerId?: string | null) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (bookingId: string) => BookingService.cancel(bookingId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: bookingQueryKeys.all });
      await refreshProfileBookingsCache(queryClient, viewerId);
    },
  });

  return {
    cancelBooking: mutation.mutateAsync,
    error: mutation.error,
    isError: mutation.isError,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
  };
}

export function useCancelBookingDetail(bookingId?: string | null, viewerId?: string | null) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (id: string) => BookingService.cancel(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: bookingQueryKeys.all });
      await Promise.all([
        refreshBookingDetailCache(queryClient, bookingId),
        refreshProfileBookingsCache(queryClient, viewerId),
      ]);
    },
  });

  return {
    cancelBooking: mutation.mutateAsync,
    error: mutation.error,
    isError: mutation.isError,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
  };
}
